/**
 * bidirectionalIntegrityGuard.js — REGRA DE OURO V2.0 (SISTEMA COMPLETO)
 * ========================================================================
 * Auto-verificação bidirecional Master ↔ Portal para TODOS os fluxos do sistema.
 *
 * Fluxos cobertos:
 *  [1] Documentos publicados (documents)        — ContractEngine/ProcurationEngine → PortalVault
 *  [2] Cadernos de autos (autos_workspaces)      — AutosDashboard → PortalVault (via workspaceIntegrityGuard)
 *  [3] GED Links (rda_ged_links)                 — FileHubArea → PortalVault (via workspaceIntegrityGuard)
 *  [4] Cadastro do cliente (clients)             — ClientForm Master ↔ PortalCompletarCadastro
 *  [5] Prontuário/Notas (clients/{id}/notes)     — ClientNotesTab → PortalProntuario
 *  [6] Processos (matters/processes)             — Master → PortalProcessos
 *  [7] Financeiro (invoices)                     — Master → PortalFinanceiro
 *
 * Cada função:
 *  - Roda automaticamente via useEffect nos componentes integrados
 *  - Loga com prefixo [INTEGRITY] no console
 *  - Retorna { ok: boolean, issues: string[] }
 *
 * Ref: Sessão 21/04/2026 — Regra de Ouro V2.0
 */

const PREFIX = '[INTEGRITY]';

// ═══════════════════════════════════════════════════════════════════════════════
// [1] DOCUMENTOS PUBLICADOS — Master publica → Portal deve exibir
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Verifica integridade dos documentos visíveis no Portal.
 * Roda em PortalVault quando docs são carregados.
 *
 * @param {Object} params
 * @param {Array}  params.docs       - documentos do onSnapshot
 * @param {string} params.clientId   - ID do cliente
 * @returns {{ ok: boolean, issues: string[] }}
 */
export function checkDocumentsIntegrity({ docs = [], clientId = '' }) {
  const issues = [];

  for (const d of (docs || [])) {
    const docClientId = String(d?.clientId || '').trim();
    const docId = String(d?.id || '').trim();

    // Doc sem clientId (dados corrompidos)
    if (!docClientId) {
      issues.push(`Doc ${docId} sem clientId — dado corrompido`);
      continue;
    }

    // Cross-contamination: doc de outro cliente aparecendo no Portal
    if (docClientId !== clientId) {
      issues.push(`Doc ${docId} pertence a cliente ${docClientId}, não ao cliente logado ${clientId} — cross-contamination`);
    }

    // Doc com clientVisible=false que não deveria aparecer
    if (d?.clientVisible === false) {
      issues.push(`Doc ${docId} tem clientVisible=false mas está na lista visível — filtro falhou`);
    }
  }

  const ok = issues.length === 0;

  if (!ok) {
    console.warn(`${PREFIX} DOCS — ${issues.length} problema(s) para cliente ${clientId}:`);
    issues.forEach((i) => console.warn(`  ⚠️ ${i}`));
  } else if (docs.length > 0) {
    console.info(`${PREFIX} DOCS OK — ${docs.length} documento(s) para cliente ${clientId} — sem inconsistências.`);
  }

  return { ok, issues };
}

// ═══════════════════════════════════════════════════════════════════════════════
// [4] CADASTRO DO CLIENTE — Portal preenche ↔ Master visualiza
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Verifica integridade do cadastro do cliente entre Portal e Master.
 * Roda em PortalCompletarCadastro após save e em ClientForm ao abrir.
 *
 * @param {Object} params
 * @param {Object} params.portalData  - dados do formulário no Portal
 * @param {Object} params.masterData  - dados do documento no Firestore (Master)
 * @param {string} params.clientId    - ID do cliente
 * @returns {{ ok: boolean, issues: string[], divergentFields: string[] }}
 */
export function checkClientProfileIntegrity({ portalData = {}, masterData = {}, clientId = '' }) {
  const issues = [];
  const divergentFields = [];

  if (!portalData || !masterData) {
    return { ok: true, issues: [], divergentFields: [] };
  }

  // Campos críticos que DEVEM ser idênticos entre Portal e Master
  const criticalPaths = [
    { path: 'qualification.physical.name', label: 'Nome Civil' },
    { path: 'qualification.physical.socialName', label: 'Nome Social' },
    { path: 'qualification.physical.cpf', label: 'CPF' },
    { path: 'contact.email', label: 'Email' },
    { path: 'contact.phonePrimary', label: 'WhatsApp' },
    { path: 'qualification.physical.profession', label: 'Profissão' },
    { path: 'location.address.street', label: 'Rua' },
    { path: 'location.address.city', label: 'Cidade' },
    { path: 'location.address.state', label: 'UF' },
    { path: 'location.address.zipCode', label: 'CEP' },
    { path: 'onboardingStatus', label: 'Status Onboarding' },
  ];

  const getNestedValue = (obj, path) => {
    return path.split('.').reduce((o, k) => (o && typeof o === 'object' ? o[k] : undefined), obj);
  };

  for (const { path, label } of criticalPaths) {
    const portalVal = String(getNestedValue(portalData, path) || '').trim();
    const masterVal = String(getNestedValue(masterData, path) || '').trim();

    if (portalVal && masterVal && portalVal !== masterVal) {
      divergentFields.push(path);
      issues.push(`${label}: Portal="${portalVal}" ≠ Master="${masterVal}" — divergência de dados`);
    }
  }

  const ok = issues.length === 0;

  if (!ok) {
    console.warn(`${PREFIX} CADASTRO — ${issues.length} divergência(s) para cliente ${clientId}:`);
    issues.forEach((i) => console.warn(`  ⚠️ ${i}`));
  } else {
    console.info(`${PREFIX} CADASTRO OK — dados do cliente ${clientId} alinhados entre Portal e Master.`);
  }

  return { ok, issues, divergentFields };
}

// ═══════════════════════════════════════════════════════════════════════════════
// [5] PRONTUÁRIO/NOTAS — Master escreve ↔ Portal visualiza
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Verifica integridade das notas do prontuário.
 * Roda em PortalProntuario quando notas são carregadas.
 *
 * @param {Object} params
 * @param {Array}  params.notes     - notas carregadas no Portal
 * @param {string} params.clientId  - ID do cliente
 * @returns {{ ok: boolean, issues: string[] }}
 */
export function checkNotesIntegrity({ notes = [], clientId = '' }) {
  const issues = [];

  for (const n of (notes || [])) {
    const noteId = String(n?.id || '').trim();

    // Nota sem createdAt (dado corrompido — não aparece na timeline)
    if (!n?.createdAt) {
      issues.push(`Nota ${noteId} sem createdAt — pode não aparecer na ordenação cronológica`);
    }

    // Nota sem category (tipo indefinido)
    const cat = String(n?.category || '').trim().toUpperCase();
    if (!cat) {
      issues.push(`Nota ${noteId} sem category — tipo indefinido`);
    }

    // Nota USER sem payload.text e sem payload.html (conteúdo vazio)
    if (cat === 'USER') {
      const text = String(n?.payload?.text || '').trim();
      const html = String(n?.payload?.html || '').trim();
      if (!text && !html) {
        issues.push(`Nota ${noteId} do tipo USER sem conteúdo (text e html vazios)`);
      }
    }
  }

  const ok = issues.length === 0;

  if (!ok) {
    console.warn(`${PREFIX} PRONTUÁRIO — ${issues.length} problema(s) para cliente ${clientId}:`);
    issues.forEach((i) => console.warn(`  ⚠️ ${i}`));
  } else if (notes.length > 0) {
    console.info(`${PREFIX} PRONTUÁRIO OK — ${notes.length} nota(s) para cliente ${clientId} — sem inconsistências.`);
  }

  return { ok, issues };
}

// ═══════════════════════════════════════════════════════════════════════════════
// [6] PROCESSOS — Master cadastra ↔ Portal visualiza
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Verifica integridade dos processos visíveis no Portal.
 * Roda em PortalProcessos quando processos são carregados.
 *
 * @param {Object} params
 * @param {Array}  params.processes  - processos carregados no Portal
 * @param {string} params.clientId   - ID do cliente
 * @returns {{ ok: boolean, issues: string[] }}
 */
export function checkProcessesIntegrity({ processes = [], clientId = '' }) {
  const issues = [];

  // Detecção de duplicatas por CNJ
  const cnjMap = new Map();
  for (const p of (processes || [])) {
    const pid = String(p?.id || '').trim();
    const cnj = String(p?.numeroCNJ || p?.cnj || p?.processNumber || p?.number || '').trim();

    // Processo sem título e sem CNJ (dados mínimos ausentes)
    const title = String(p?.title || p?.caseTitle || p?.name || p?.assunto || '').trim();
    if (!title && !cnj) {
      issues.push(`Processo ${pid} sem título e sem CNJ — dados mínimos ausentes`);
    }

    // Duplicata por CNJ
    if (cnj) {
      const key = cnj.replace(/\D/g, '');
      if (key && cnjMap.has(key)) {
        issues.push(`CNJ duplicado: "${cnj}" aparece nos IDs ${cnjMap.get(key)} e ${pid}`);
      } else if (key) {
        cnjMap.set(key, pid);
      }
    }

    // Cross-contamination via clientId
    const processClientId = String(p?.clientId || p?.clientRef || p?.client || '').trim();
    if (processClientId && processClientId !== clientId) {
      // Verificar se está nos partiesRefs antes de reportar
      const parties = Array.isArray(p?.partiesRefs) ? p.partiesRefs : [];
      const isParty = parties.some((ref) => {
        const refId = typeof ref === 'string' ? ref : String(ref?.clientId || '').trim();
        return refId === clientId;
      });
      if (!isParty) {
        issues.push(`Processo ${pid} com clientId=${processClientId} ≠ ${clientId} e cliente não está em partiesRefs — cross-contamination`);
      }
    }
  }

  const ok = issues.length === 0;

  if (!ok) {
    console.warn(`${PREFIX} PROCESSOS — ${issues.length} problema(s) para cliente ${clientId}:`);
    issues.forEach((i) => console.warn(`  ⚠️ ${i}`));
  } else if (processes.length > 0) {
    console.info(`${PREFIX} PROCESSOS OK — ${processes.length} processo(s) para cliente ${clientId} — sem inconsistências.`);
  }

  return { ok, issues };
}

// ═══════════════════════════════════════════════════════════════════════════════
// [7] FINANCEIRO — Master lança ↔ Portal visualiza
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Verifica integridade dos dados financeiros visíveis no Portal.
 * Roda em PortalFinanceiro quando faturas são carregadas.
 *
 * @param {Object} params
 * @param {Array}  params.invoices  - faturas carregadas no Portal
 * @param {string} params.clientId  - ID do cliente
 * @returns {{ ok: boolean, issues: string[] }}
 */
export function checkFinanceIntegrity({ invoices = [], clientId = '' }) {
  const issues = [];

  for (const inv of (invoices || [])) {
    const invId = String(inv?.id || '').trim();
    const invClientId = String(inv?.clientId || '').trim();

    // Cross-contamination
    if (invClientId && invClientId !== clientId) {
      issues.push(`Fatura ${invId} pertence a cliente ${invClientId}, não ao cliente logado ${clientId} — cross-contamination`);
    }

    // Fatura sem valor
    const amount = Number(inv?.amount || inv?.totalCents || inv?.value || 0);
    if (!amount || amount <= 0) {
      issues.push(`Fatura ${invId} sem valor definido (amount=${amount})`);
    }

    // Fatura sem status
    const status = String(inv?.status || '').trim().toUpperCase();
    if (!status) {
      issues.push(`Fatura ${invId} sem status — estado indefinido`);
    }

    // Fatura sem dueDate
    if (!inv?.dueDate) {
      issues.push(`Fatura ${invId} sem data de vencimento`);
    }
  }

  const ok = issues.length === 0;

  if (!ok) {
    console.warn(`${PREFIX} FINANCEIRO — ${issues.length} problema(s) para cliente ${clientId}:`);
    issues.forEach((i) => console.warn(`  ⚠️ ${i}`));
  } else if (invoices.length > 0) {
    console.info(`${PREFIX} FINANCEIRO OK — ${invoices.length} fatura(s) para cliente ${clientId} — sem inconsistências.`);
  }

  return { ok, issues };
}

// ═══════════════════════════════════════════════════════════════════════════════
// VERIFICAÇÃO GLOBAL — Roda todas as verificações de uma vez
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Executa TODAS as verificações de integridade bidirecional.
 * Útil para testes automatizados e diagnóstico completo.
 *
 * @param {Object} params
 * @param {string} params.clientId
 * @param {Array}  params.docs
 * @param {Array}  params.notes
 * @param {Array}  params.processes
 * @param {Array}  params.invoices
 * @param {Object} params.portalData
 * @param {Object} params.masterData
 * @returns {{ ok: boolean, totalIssues: number, results: Object }}
 */
export function runFullIntegrityCheck({
  clientId = '',
  docs = [],
  notes = [],
  processes = [],
  invoices = [],
  portalData = null,
  masterData = null,
}) {
  const results = {};

  results.documents = checkDocumentsIntegrity({ docs, clientId });
  results.profile = portalData && masterData
    ? checkClientProfileIntegrity({ portalData, masterData, clientId })
    : { ok: true, issues: [] };
  results.notes = checkNotesIntegrity({ notes, clientId });
  results.processes = checkProcessesIntegrity({ processes, clientId });
  results.finance = checkFinanceIntegrity({ invoices, clientId });

  const totalIssues = Object.values(results).reduce((sum, r) => sum + (r?.issues?.length || 0), 0);
  const ok = totalIssues === 0;

  if (!ok) {
    console.warn(`${PREFIX} GLOBAL — ${totalIssues} problema(s) total para cliente ${clientId}.`);
  } else {
    console.info(`${PREFIX} GLOBAL OK — Todas as verificações bidirecionais passaram para cliente ${clientId}.`);
  }

  return { ok, totalIssues, results };
}
